import { useCollabItemContext } from 'components/CollabItem/CollabItem'
import { Item } from 'components/CollabItem/Item'
import { IconButton } from 'components/IconButton/IconButton'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Item as MenuItem } from '@react-stately/collections'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useCommentPermissions } from '../useCommentPermissions'
import { ReactComponent as ThreeDotsIcon } from 'assets/icons/threeDots.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit16.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash16.svg'
import { ReplyItemProps } from './Types'
import { Menu } from 'components/Menu/Menu'
import { Key } from 'react'
import { ReactControl } from '../ReactControl/ReactControl'
import { ItemEditForm } from '../ItemEditForm/ItemEditForm'
import dayjs from 'dayjs'
import { dateFormat } from 'utils/dates'
import { parseUrlsInString } from 'utils/urlUtils'
import { parseCommentMentionsToHTML } from 'utils/comments'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import { Comment } from 'types'
import ItemStyles from 'components/CollabItem/CollabItem.module.scss'
import CommentStyles from 'components/CommentV2/CommentItem/CommentItem.module.scss'
import cx from 'classnames'
import { UnreadDot } from 'components/CollabItem/UnreadDot/UnreadDot'

interface ComponentProps {
  comment: Comment
}

export const ItemHeader = ({ comment }: ComponentProps) => (
  <Item.Header>
    <Item.Avatar url={comment.user.avatar || undefined} name={comment.user.display_name} />
    <p>
      <Item.Actor name={comment.user.display_name} actorId={comment.user.id} capitalize />{' '}
      <span className={CommentStyles.metaData}>{dayjs(comment.created_at).format('MMM D h:mma')}</span>
    </p>
  </Item.Header>
)

export const ItemBody = ({ comment }: ComponentProps) => {
  const richText = useFlipperFlag('richtext_comments')

  return (
    <Item.Body>
      {richText ? (
        <ProseDisplay html={comment.html_comment} />
      ) : (
        <div dangerouslySetInnerHTML={{ __html: parseUrlsInString(parseCommentMentionsToHTML(comment.comment)) }} />
      )}
    </Item.Body>
  )
}

export const ItemFooter = ({ comment }: ComponentProps) => (
  <Item.Footer>{dayjs(comment.created_at).format(`${dateFormat(comment.created_at)} h:mm a`)}</Item.Footer>
)

const ReplyItem = observer(({ comment, inCleanupMode }: ReplyItemProps) => {
  const rootStore = useRootStore()
  const { canReact, canEdit, canDelete } = useCommentPermissions(comment, inCleanupMode)
  const { editing, setEditing } = useCollabItemContext()
  const isEditing = editing?.id === comment.id
  const disabledKeys = new Set<string>()
  if (!canEdit) disabledKeys.add('edit')
  if (!canDelete) disabledKeys.add('delete')
  const showActionMenu = canEdit || canDelete

  const handleAction = (action: Key) => {
    switch (action) {
      case 'edit':
        setEditing({ type: 'Comment', id: comment.id })
        rootStore.comments.select(comment.commentable_id as number)
        break
      case 'delete':
        rootStore.comments.delete(comment.id)
    }
  }

  const toggleReaction = (reaction: string) => {
    rootStore.comments.toggleReaction(comment.id, reaction)
  }

  return (
    <Item.Root
      editing={isEditing}
      className={cx(CommentStyles.itemRoot, CommentStyles.replyItemRoot, {
        [CommentStyles.isEditing]: isEditing,
      })}
    >
      <UnreadDot isUnread={!comment.read_status} className={CommentStyles.unreadDot} />
      {!isEditing && (
        <Item.ControlGroup>
          {showActionMenu && (
            <Item.Control tooltipTitle="More options">
              <MenuTrigger placement="bottom-end">
                <IconButton data-title="More options" className={ItemStyles.controlItem} icon={ThreeDotsIcon} />
                <Menu aria-label="More actions" disabledKeys={disabledKeys} onAction={handleAction}>
                  <MenuItem key="edit" textValue="Edit">
                    <EditIcon />
                    Edit
                  </MenuItem>
                  <MenuItem key="delete" textValue="Delete">
                    <TrashIcon />
                    Delete
                  </MenuItem>
                </Menu>
              </MenuTrigger>
            </Item.Control>
          )}
          {canReact && <ReactControl comment={comment} />}
        </Item.ControlGroup>
      )}
      <ItemHeader comment={comment} />
      {isEditing && <ItemEditForm comment={comment} />}
      {!isEditing && (
        <>
          <ItemBody comment={comment} />
          <Item.Reactions reactions={comment.reactions} onToggleReaction={toggleReaction} />
        </>
      )}
    </Item.Root>
  )
})

export { ReplyItem }
